import { useId, useState } from 'react';
import { normaliserNumeroJoignable, telephoneLisible, type CandidatureDetail } from '@rh/shared';
import { appel } from '../api';

/**
 * Numéro de téléphone ajouté à la main sur la fiche (demande du 2026-09-21).
 *
 * Jusqu'ici, les autres numéros venaient tous des CV : quand le candidat
 * rappelait d'un autre poste, laissait celui d'un proche ou corrigeait le
 * sien, l'équipe n'avait nulle part où l'écrire. Le numéro s'affiche à côté du
 * principal et se compose comme lui ; il ne rapproche **jamais** deux fiches —
 * le numéro principal reste la seule clé de dédoublonnage — et il ne remplace
 * pas le numéro de la fiche.
 */

interface Props {
  candidatureId: string;
  onChange: (fiche: CandidatureDetail) => void;
}

export function AjouterTelephone({ candidatureId, onChange }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [numero, setNumero] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const champ = useId();

  function fermer(): void {
    setOuvert(false);
    setNumero('');
    setErreur(null);
  }

  async function ajouter(): Promise<void> {
    // Le serveur juge, mais un numéro manifestement incomplet ne mérite pas un
    // aller-retour : le message est le même des deux côtés.
    if (!normaliserNumeroJoignable(numero)) {
      setErreur('Numéro incompréhensible : 8 chiffres, ou +216 / +33… pour un numéro étranger');
      return;
    }
    setEnvoi(true);
    setErreur(null);
    try {
      onChange(
        await appel<CandidatureDetail>(`/candidatures/${candidatureId}/telephones`, {
          method: 'POST',
          body: JSON.stringify({ telephone: numero.trim() }),
        }),
      );
      fermer();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Ajout impossible');
    } finally {
      setEnvoi(false);
    }
  }

  if (!ouvert) {
    return (
      <button type="button" className="lien ajouter-numero" onClick={() => setOuvert(true)}>
        + Ajouter un numéro
      </button>
    );
  }

  return (
    <span className="ajout-numero">
      <label className="visuellement-cache" htmlFor={champ}>
        Numéro à ajouter
      </label>
      <input
        id={champ}
        type="tel"
        inputMode="tel"
        value={numero}
        maxLength={30}
        disabled={envoi}
        autoFocus
        placeholder="22 123 456"
        onChange={(e) => setNumero(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void ajouter();
          if (e.key === 'Escape') fermer();
        }}
      />
      <button type="button" className="bouton-second" disabled={envoi || !numero.trim()} onClick={() => void ajouter()}>
        {envoi ? 'Ajout…' : 'Ajouter'}
      </button>
      <button type="button" className="lien" disabled={envoi} onClick={fermer}>
        Annuler
      </button>
      {erreur && <span className="erreur-numero">{erreur}</span>}
    </span>
  );
}

/**
 * Retire un numéro **ajouté à la main**. Les numéros lus dans les CV n'ont pas
 * ce bouton : la lecture suivante les remettrait, et un bouton qui ne tient
 * pas sa promesse est pire que pas de bouton.
 */
export function RetirerTelephone({
  candidatureId,
  numero,
  onChange,
}: Props & { numero: string }) {
  const [envoi, setEnvoi] = useState(false);

  async function retirer(): Promise<void> {
    if (!window.confirm(`Retirer le numéro ${telephoneLisible(numero)} de cette fiche ?`)) return;
    setEnvoi(true);
    try {
      onChange(
        await appel<CandidatureDetail>(`/candidatures/${candidatureId}/telephones/retirer`, {
          method: 'POST',
          body: JSON.stringify({ telephone: numero }),
        }),
      );
    } catch {
      // Le numéro reste affiché : la fiche n'a pas changé, rien à annoncer de
      // plus qu'un geste qui n'a pas pris.
    } finally {
      setEnvoi(false);
    }
  }

  return (
    <button
      type="button"
      className="retirer-numero"
      disabled={envoi}
      aria-label={`Retirer le numéro ${telephoneLisible(numero)}`}
      title="Retirer ce numéro"
      onClick={() => void retirer()}
    >
      ×
    </button>
  );
}
